import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAvatar from "components/MDAvatar";
import FloatSemanticMessage from "components/FloatSemanticMessage";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  linkGoogleToCurrentUserFirebase,
  linkPasswordToCurrentUserFirebase,
  subscribeToAuthChanges,
} from "services/firebase";

function AccountConnections() {
  const location = useLocation();
  const [authenticatedUser, setAuthenticatedUser] = useState(undefined);
  const [selectedSection, setSelectedSection] = useState("profile");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  const [isLinkingPassword, setIsLinkingPassword] = useState(false);
  const [messageState, setMessageState] = useState({
    open: false,
    severity: "info",
    title: "",
    message: "",
  });

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setAuthenticatedUser(user);
    });

    return unsubscribe;
  }, []);

  const providerIds = authenticatedUser?.providerData?.map((provider) => provider.providerId) || [];
  const hasGoogleLinked = providerIds.includes("google.com");
  const hasPasswordLinked = providerIds.includes("password");
  const userName = authenticatedUser?.displayName || "Usuario";
  const userEmail = authenticatedUser?.email || "-";
  const userPhoto = authenticatedUser?.photoURL || "";

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setSelectedSection(searchParams.get("tab") === "connections" ? "connections" : "profile");
  }, [location.search]);

  const showMessage = (title, message, severity = "error") => {
    setMessageState({ open: true, severity, title, message });
  };

  const closeMessage = () => {
    setMessageState((current) => ({ ...current, open: false }));
  };

  const resolveLinkErrorMessage = (error, provider) => {
    const authCode = error?.code;

    switch (authCode) {
      case "auth/provider-already-linked":
        return `A conta ${provider} ja esta conectada.`;
      case "auth/credential-already-in-use":
        return `Essa credencial ${provider} ja esta em uso em outra conta.`;
      case "auth/email-already-in-use":
        return "Esse e-mail ja esta em uso em outra conta.";
      case "auth/popup-closed-by-user":
        return "Vinculacao com Google cancelada.";
      case "auth/popup-blocked":
        return "O navegador bloqueou o popup do Google. Libere popups e tente novamente.";
      case "auth/weak-password":
        return "A senha precisa ter pelo menos 6 caracteres.";
      default:
        return "Nao foi possivel concluir a vinculacao agora. Tente novamente.";
    }
  };

  const handleConnectGoogle = async () => {
    setIsLinkingGoogle(true);

    try {
      await linkGoogleToCurrentUserFirebase();
      showMessage("Google conectado", "Sua conta Google foi vinculada com sucesso.", "success");
    } catch (error) {
      showMessage("Falha na vinculacao", resolveLinkErrorMessage(error, "Google"), "error");
    } finally {
      setIsLinkingGoogle(false);
    }
  };

  const handleConnectPassword = async (event) => {
    event.preventDefault();

    if (!authenticatedUser?.email) {
      showMessage("Falha na vinculacao", "Nao foi encontrado um e-mail para vincular senha.", "error");
      return;
    }

    if (!password) {
      showMessage("Falha na vinculacao", "Informe uma senha para continuar.", "error");
      return;
    }

    if (password.length < 6) {
      showMessage("Falha na vinculacao", "A senha precisa ter pelo menos 6 caracteres.", "error");
      return;
    }

    if (password !== confirmPassword) {
      showMessage("Falha na vinculacao", "A confirmacao da senha nao confere.", "error");
      return;
    }

    setIsLinkingPassword(true);

    try {
      await linkPasswordToCurrentUserFirebase(authenticatedUser.email, password);
      setPassword("");
      setConfirmPassword("");
      showMessage("Senha conectada", "Agora voce tambem pode entrar com e-mail e senha.", "success");
    } catch (error) {
      showMessage("Falha na vinculacao", resolveLinkErrorMessage(error, "de senha"), "error");
    } finally {
      setIsLinkingPassword(false);
    }
  };

  const renderSectionContent = () => {
    if (selectedSection === "profile") {
      return (
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5" fontWeight="medium" mb={2}>
              Dados do perfil
            </MDTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="button" color="text" fontWeight="bold">
                    Nome
                  </MDTypography>
                  <MDTypography variant="button" sx={{ display: "block", mt: 0.5 }}>
                    {userName}
                  </MDTypography>
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography variant="button" color="text" fontWeight="bold">
                    E-mail
                  </MDTypography>
                  <MDTypography variant="button" sx={{ display: "block", mt: 0.5 }}>
                    {userEmail}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox
                  p={2}
                  borderRadius="xl"
                  bgColor="light"
                  sx={{ height: "100%", minHeight: "10rem" }}
                >
                  <MDTypography variant="button" color="text" fontWeight="bold">
                    Metodos de acesso
                  </MDTypography>
                  <MDBox mt={2} display="flex" flexWrap="wrap" gap={1}>
                    <MDBox px={1.5} py={0.75} borderRadius="lg" bgColor={hasGoogleLinked ? "success" : "dark"}>
                      <MDTypography variant="caption" color="white" fontWeight="bold">
                        Google {hasGoogleLinked ? "conectado" : "nao conectado"}
                      </MDTypography>
                    </MDBox>
                    <MDBox px={1.5} py={0.75} borderRadius="lg" bgColor={hasPasswordLinked ? "success" : "dark"}>
                      <MDTypography variant="caption" color="white" fontWeight="bold">
                        Senha {hasPasswordLinked ? "conectada" : "nao conectada"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      );
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    Google
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    Use sua conta Google para entrar.
                  </MDTypography>
                </MDBox>
                <MDBox
                  px={1.5}
                  py={0.5}
                  borderRadius="lg"
                  bgColor={hasGoogleLinked ? "success" : "light"}
                >
                  <MDTypography variant="caption" fontWeight="bold" color={hasGoogleLinked ? "white" : "dark"}>
                    {hasGoogleLinked ? "Conectado" : "Nao conectado"}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDButton
                variant={hasGoogleLinked ? "outlined" : "gradient"}
                color="info"
                onClick={handleConnectGoogle}
                disabled={hasGoogleLinked || isLinkingGoogle}
                startIcon={<Icon>link</Icon>}
              >
                {isLinkingGoogle ? "Conectando..." : hasGoogleLinked ? "Google conectado" : "Conectar Google"}
              </MDButton>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <MDBox p={3} component="form" onSubmit={handleConnectPassword}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    E-mail e senha
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    Conecte uma senha a este e-mail: {userEmail}
                  </MDTypography>
                </MDBox>
                <MDBox
                  px={1.5}
                  py={0.5}
                  borderRadius="lg"
                  bgColor={hasPasswordLinked ? "success" : "light"}
                >
                  <MDTypography variant="caption" fontWeight="bold" color={hasPasswordLinked ? "white" : "dark"}>
                    {hasPasswordLinked ? "Conectado" : "Nao conectado"}
                  </MDTypography>
                </MDBox>
              </MDBox>
              {!hasPasswordLinked && (
                <>
                  <MDBox mb={2}>
                    <MDInput
                      type={showPassword ? "text" : "password"}
                      label="Nova senha"
                      fullWidth
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              <Icon>{showPassword ? "visibility_off" : "visibility"}</Icon>
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </MDBox>
                  <MDBox mb={3}>
                    <MDInput
                      type={showConfirmPassword ? "text" : "password"}
                      label="Confirmar senha"
                      fullWidth
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              <Icon>{showConfirmPassword ? "visibility_off" : "visibility"}</Icon>
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </MDBox>
                </>
              )}
              <MDButton
                type="submit"
                variant={hasPasswordLinked ? "outlined" : "gradient"}
                color="info"
                disabled={hasPasswordLinked || isLinkingPassword}
                startIcon={<Icon>password</Icon>}
              >
                {isLinkingPassword
                  ? "Conectando..."
                  : hasPasswordLinked
                    ? "Senha conectada"
                    : "Conectar senha"}
              </MDButton>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <DashboardLayout>
      <FloatSemanticMessage
        open={messageState.open}
        title={messageState.title}
        message={messageState.message}
        severity={messageState.severity}
        onClose={closeMessage}
      />
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Card>
          <MDBox
            p={3}
            sx={{
              backgroundImage:
                "linear-gradient(135deg, rgba(73,163,241,0.92), rgba(26,115,232,0.88))",
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <MDAvatar src={userPhoto} alt={userName} size="xl" shadow="sm" />
              </Grid>
              <Grid item xs>
                <MDTypography variant="h4" color="white" fontWeight="medium">
                  {userName}
                </MDTypography>
                <MDTypography variant="button" color="white" sx={{ opacity: 0.85 }}>
                  {userEmail}
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>
      {renderSectionContent()}
    </DashboardLayout>
  );
}

export default AccountConnections;
